import { getEffortRateType } from "../../helper/profileCalculations";
import {
  Avatar,
  Badge,
  Button,
  Icon,
  PageHeader,
} from "../../components/design";
import useTranslator from "../../hooks/useTranslator";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, TextArea } from "../../components/design";
import {
  calculateOverlayScore,
  getUserDisplayName,
  getCustomIncomeDetails,
  getFieldForPolicies,
  getKycResolutionStatus,
  getRentPercentage,
  haveValue,
  tenantHasMissingDniNie,
  toast,
} from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import TenantContext from "../../context/tenant/TenantContext";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/en-gb";
import AuthContext from "../../context/auth/AuthContext";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import InsurabilityBadge from "../../components/Property/InsurabilityBadge";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import axiosInstance from "../../services/authWrapper";
import { transformAwardSnapshot } from "../../Utils/transformAwardSnapshot";
import { getMissingDocsFromSnapshot } from "../../Utils/getMissingDocsFromSnapshot";
import VerdictPanel, {
  InfoIcon,
  LockIcon,
  VerdictInlineLine,
  withColon,
} from "../../components/Property/VerdictPanel";
import {
  buildHardFailWarnings,
  buildBlockingRows,
  getHardFailDisplay,
  isGuarantorOutsideSpainHardFail,
} from "../../helper/verdictHelpers";
import { getSavedIncomeComponents } from "../../helper/payslipSelection";
import "../../styles/linked-report-overview.css";

const ReportOverview = (props = {}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const {
    getPropertyDetails,
    getTenantExtraData,
    deaWorkspace,
    markProfileResolved,
    getProfileResolutionStatus,
    unmarkProfileResolved,
  } = useContext(LandLordContext);
  const { user } = useContext(AuthContext);
  const { getZipOfMultipleTenantDocs } = useContext(TenantContext);
  const {
    property_id: propertyIdProp,
    sharedprofileid: sharedprofileidProp,
    rent: rentProp,
    redirectUrl: redirectUrlProp,
    selection: selectionRawProp,
    awardSnapshotCompleted: awardSnapshotCompletedProp,
    historyEventId: historyEventIdProp,
    onResolutionChange: onResolutionChangeProp,
    insuranceSimulation,
    guarantorIds: guarantorIdsProp,
    guarantorid: guarantorIdProp,
  } = props;

  const queryParameters =
    props.queryParameters ?? new URLSearchParams(window.location.search);
  const property_id = propertyIdProp ?? queryParameters.get("property");
  const sharedprofileid =
    sharedprofileidProp ?? queryParameters.getAll("sharedprofileid");
  const sharedProfileIdsKey = sharedprofileid.join(",");
  const rentVal = rentProp ?? queryParameters.get("rent");
  const redirectUrl = redirectUrlProp ?? queryParameters.get("redirectUrl");
  const selectionRaw = selectionRawProp ?? queryParameters.get("selection");

  const selectionSelectedIds = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.flatMap((entry) => {
        if (entry.type === "tenant") return [entry.tenantId].filter(Boolean);
        return (entry.participants || [])
          .map((p) => p.tenantId)
          .filter(Boolean);
      });
    } catch {
      return null;
    }
  }, [selectionRaw]);

  const awardSnapshotCompleted =
    awardSnapshotCompletedProp ??
    queryParameters.get("awardSnapshotCompleted") === "true";
  const historyEventId =
    historyEventIdProp ?? queryParameters.get("historyEventId");

  const selectionSelectedGuarantorIds = useMemo(() => {
    if (!selectionRaw) return null;
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.flatMap((entry) => {
        if (entry.type === "tenant") return entry.guarantorIds || [];
        return (entry.participants || []).flatMap((p) => p.guarantorIds || []);
      });
    } catch {
      return null;
    }
  }, [selectionRaw]);

  // Explicit guarantor ids from `guarantorid=` URL params (nested guarantors
  // plus application participants flagged as guarantors). Merged with the
  // nested ids in selection JSON so old links keep working. Also honours the
  // drawer path, which passes ids as props instead of window URL params.
  const urlGuarantorIds = useMemo(() => {
    try {
      const fromProps = [
        ...(Array.isArray(guarantorIdsProp)
          ? guarantorIdsProp
          : haveValue(guarantorIdsProp)
            ? [guarantorIdsProp]
            : []),
        ...(Array.isArray(guarantorIdProp)
          ? guarantorIdProp
          : haveValue(guarantorIdProp)
            ? [guarantorIdProp]
            : []),
      ];
      const raw = [
        ...fromProps,
        ...queryParameters.getAll("guarantorid"),
        ...queryParameters.getAll("guarantorId"),
        ...queryParameters.getAll("guarantorids"),
        ...queryParameters.getAll("guarantorIds"),
      ];
      const split = raw
        .flatMap((v) => String(v).split(","))
        .map((s) => s.trim())
        .filter(Boolean);
      return [
        ...new Set(
          [...split, ...(selectionSelectedGuarantorIds || [])]
            .map((id) => String(id))
            .filter(Boolean)
        ),
      ];
    } catch {
      return [...(selectionSelectedGuarantorIds || [])].map(String);
    }
  }, [
    selectionRaw,
    selectionSelectedGuarantorIds,
    guarantorIdsProp,
    guarantorIdProp,
    queryParameters,
  ]);

  const todaysDate = (
    createdAt = null,
    locale = localStorage.getItem("i18nextLng")
  ) => {
    moment.locale(locale);
    return moment(createdAt || undefined).format("D MMMM YYYY");
  };
  const STATUS_STYLES = {
    approved: {
      backgroundColor: "var(--lime-soft)",
      color: "var(--ink)",
      border: "var(--border-1) solid var(--lime)",
      text: _t("approved"),
      img: "/images/tick-new.svg",
      insurable: _t("insurable"),
    },
    review: {
      backgroundColor: "var(--warning-soft)",
      color: "var(--warning-text)",
      border: "var(--border-1) solid var(--warning-line)",
      text: _t("manualReview"),
      img: "/images/warning.svg",
      insurable: _t("insurable"),
    },
    rejected: {
      backgroundColor: "var(--danger-soft)",
      color: "var(--danger-text)",
      border: "var(--border-1) solid var(--danger)",
      text: _t("declined"),
      img: "/images/notApproved.svg",
      insurable: _t("notInsurable"),
    },
  };

  const [status, setStatus] = useState("approved");
  const [addressVal, setAddressVal] = useState("");
  const [tenantData, setTenantData] = useState([]);
  const [showLocalLoading, setShowLocalLoading] = useState(true);
  const [individualError, setIndividualError] = useState(false);
  const [individualWarnings, setIndividualWarnings] = useState(false);
  const [lowScoreCheck, setLowScoreCheck] = useState(false);
  const [higherScoreProfile, setHigherScoreProfile] = useState(false);
  const [missingDniNieWarning, setMissingDniNieWarning] = useState({});
  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedResolveTenantIds, setSelectedResolveTenantIds] = useState([]);
  const [resolvingDocs, setResolvingDocs] = useState(false);
  const [showResolveNoteModal, setShowResolveNoteModal] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const [individualResolutions, setIndividualResolutions] = useState({});
  const [kycWarnings, setKycWarnings] = useState({});
  const [rawTenantsById, setRawTenantsById] = useState({});
  const [isKycWarningsLoading, setIsKycWarningsLoading] = useState(false);
  const isVerdictLoading = isKycWarningsLoading;
  const [expandedDocs, setExpandedDocs] = useState({});
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [expandedTenantRows, setExpandedTenantRows] = useState({});
  const toggleDocsExpanded = (id) =>
    setExpandedDocs((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleTenantRow = (key) =>
    setExpandedTenantRows((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedProfileIdMap = useMemo(() => {
    if (!selectionRaw) return {};
    try {
      const model = JSON.parse(decodeURIComponent(selectionRaw));
      return model.reduce((map, entry) => {
        const sharedProfileId = entry?.sharedProfileId;
        if (!sharedProfileId) return map;
        if (entry.type === "tenant") {
          if (entry.tenantId) map[entry.tenantId] = sharedProfileId;
          (entry.guarantorIds || []).forEach((gid) => {
            if (gid) map[gid] = sharedProfileId;
          });
          return map;
        }
        (entry.participants || []).forEach((p) => {
          if (p?.tenantId) map[p.tenantId] = sharedProfileId;
          (p?.guarantorIds || []).forEach((gid) => {
            if (gid) map[gid] = sharedProfileId;
          });
        });
        return map;
      }, {});
    } catch {
      return {};
    }
  }, [selectionRaw]);

  const [idToSharedProfileId, setIdToSharedProfileId] =
    useState(selectedProfileIdMap);

  useEffect(() => {
    if (!Object.keys(selectedProfileIdMap).length) return;
    setIdToSharedProfileId((prev) => ({ ...prev, ...selectedProfileIdMap }));
  }, [selectedProfileIdMap]);

  // Insurance is decided on the group by the insurance simulation, the same
  // way as in the recommendation report. Only passed in from the drawer.
  const storedInsuranceState = useMemo(() => {
    const key = queryParameters.get("insuranceStateKey");
    if (!key) return null;
    try {
      return JSON.parse(sessionStorage.getItem(key));
    } catch {
      return null;
    }
    // eslint-disable-next-line
  }, []);
  const insuranceState = insuranceSimulation ?? storedInsuranceState;
  const isInsuranceSimulationActive = !!insuranceSimulation;
  const isSimulationLoading = !!insuranceSimulation?.loading;
  useEffect(() => {
    if (!isInsuranceSimulationActive) return;
    insuranceSimulation.run();
    // eslint-disable-next-line
  }, [isInsuranceSimulationActive, sharedProfileIdsKey]);

  useEffect(() => {
    const loadData = async () => {
      setShowLocalLoading(true);

      let propertyData = null;
      if (haveValue(property_id)) {
        try {
          const res = await getPropertyDetails(property_id);
          propertyData = res.properties;
          setAddressVal(propertyData);
        } catch (err) {
          console.log(err);
        }
      }

      if (
        !haveValue(sharedprofileid) &&
        !awardSnapshotCompleted &&
        !historyEventId
      ) {
        setShowLocalLoading(false);
        return;
      }

      let tenantDataLoaded = false;
      try {
        let rawTenants = [];

        if (
          (awardSnapshotCompleted || historyEventId) &&
          haveValue(property_id)
        ) {
          try {
            const snapshotRes = historyEventId
              ? await axiosInstance.get(
                  `${process.env.REACT_APP_SERVER_URL}/users/history-award-snapshot/${historyEventId}`
                )
              : await axiosInstance.post(
                  `${process.env.REACT_APP_SERVER_URL}/users/award-snapshot`,
                  { propertyId: property_id }
                );
            const snapshotData = snapshotRes.data?.data;
            const snapshotProfiles = transformAwardSnapshot(snapshotData);

            const snapshotCreatedAt =
              propertyData?.award_snapshot_info?.created_at;

            snapshotProfiles.forEach((profile) => {
              const tenantData = profile.tenantid;
              if (tenantData) {
                tenantData._snapshotMissingDocs = getMissingDocsFromSnapshot(
                  tenantData,
                  deaWorkspace,
                  snapshotCreatedAt
                );
                if (tenantData.gurantor_id) {
                  tenantData.gurantor_id.forEach((g) => {
                    if (g && typeof g === "object") {
                      g._snapshotMissingDocs = getMissingDocsFromSnapshot(
                        g,
                        deaWorkspace,
                        snapshotCreatedAt
                      );
                    }
                  });
                }
              }
              if (profile.application_id?.participants) {
                profile.application_id.participants.forEach((p) => {
                  const pt = p.tenant_info;
                  if (pt) {
                    pt._snapshotMissingDocs = getMissingDocsFromSnapshot(
                      pt,
                      deaWorkspace,
                      snapshotCreatedAt
                    );
                    if (pt.gurantor_id) {
                      pt.gurantor_id.forEach((g) => {
                        if (g && typeof g === "object") {
                          g._snapshotMissingDocs = getMissingDocsFromSnapshot(
                            g,
                            deaWorkspace,
                            snapshotCreatedAt
                          );
                        }
                      });
                    }
                  }
                });
              }
            });

            snapshotProfiles.forEach((profile) => {
              if (profile.tenantid) {
                rawTenants.push(profile.tenantid);
              }
              if (profile.application_id?.participants) {
                profile.application_id.participants.forEach((p) => {
                  if (p.tenant_info) {
                    rawTenants.push(p.tenant_info);
                  }
                });
              }
            });

            const snapshotIdMap = {};
            snapshotProfiles.forEach((profile) => {
              const spId = profile._id;
              if (profile.tenantid) {
                const tid =
                  profile.tenantid?._id ??
                  profile.tenantid?.id ??
                  profile.tenantid?.tenant_id;
                if (tid) snapshotIdMap[tid] = spId;
                (profile.tenantid?.gurantor_id || []).forEach((g) => {
                  if (g?._id) snapshotIdMap[g._id] = spId;
                });
              }
              (profile.application_id?.participants || []).forEach((p) => {
                const pt = p.tenant_info;
                if (pt?._id) snapshotIdMap[pt._id] = spId;
                (pt?.gurantor_id || []).forEach((g) => {
                  if (g?._id) snapshotIdMap[g._id] = spId;
                });
              });
            });
            if (Object.keys(snapshotIdMap).length) {
              setIdToSharedProfileId((prev) => ({ ...prev, ...snapshotIdMap }));
            }

            if (selectionSelectedIds) {
              rawTenants = rawTenants.filter((t) =>
                selectionSelectedIds.includes(t?.id ?? t?.tenant_id ?? t?._id)
              );
            }
          } catch (snapshotError) {
            console.error(
              "[ReportOverview] snapshot fetch failed, falling back to live data",
              snapshotError
            );
          }
        }

        if (rawTenants.length === 0 && haveValue(sharedprofileid)) {
          const tenantRes = await getTenantExtraData(sharedprofileid);
          rawTenants = tenantRes?.success_res ?? [];
          if (selectionSelectedIds) {
            rawTenants = rawTenants.filter((t) =>
              selectionSelectedIds.includes(t?._id)
            );
          }
        }

        if (rawTenants.length > 0) {
          {
            const map = {};
            rawTenants.forEach((t) => {
              const ids = [t?._id, t?.id, t?.tenant_id].filter(Boolean);
              ids.forEach((id) => {
                map[id] = t;
                map[String(id)] = t;
              });
            });
            setRawTenantsById(map);
          }
          await handleTenantResponse(rawTenants);
          tenantDataLoaded = true;
        }
      } catch (err) {
        console.log(err);
      } finally {
        if (!tenantDataLoaded) setShowLocalLoading(false);
      }
    };

    loadData();
  }, [
    property_id,
    sharedProfileIdsKey,
    awardSnapshotCompleted,
    historyEventId,
    selectionSelectedIds,
    deaWorkspace,
    dataRefreshKey,
  ]);

  useEffect(() => {
    if (tenantData.length === 0) return;

    const result = tenantData.reduce(
      (acc, item) => {
        if (item?.errorEntries?.length > 0) {
          acc.isHardFail = true;
          acc.errorsByLabel[item.label] = item.errorEntries;
        }

        if (item?.vals?.DOCUMENTATION > 0) {
          acc.warningsById[item._id] = {
            label: item.label,
            docsMissing: item?.vals?.MISSING_DOCS,
            isResolved: !!item?.isResolved,
          };
        }

        const hasKycHardFail = (item?.errorEntries || []).some(
          ([, value]) => value?.label === "KYCHardFail"
        );
        if (item?.hasMissingDniNie && !hasKycHardFail) {
          acc.missingDniNieByLabel[item.label] = true;
        }
        return acc;
      },
      {
        isHardFail: false,
        errorsByLabel: {},
        warningsById: {},
        missingDniNieByLabel: {},
      }
    );
    setMissingDniNieWarning(result?.missingDniNieByLabel);
    console.log(result, "checkResult");
    setIndividualError(result?.errorsByLabel);
    setIndividualWarnings(result?.warningsById);
    const highPointProfile = tenantData.reduce((max, current) => {
      if (!max) return current;

      return current?.DEA_SCORE?.finalScore > max?.DEA_SCORE?.finalScore
        ? current
        : max;
    }, null);
    const profilesHaveMissingDocs = tenantData.some(
      (item) => item?.vals?.DOCUMENTATION > 0 && !item?.isResolved
    );

    setLowScoreCheck(highPointProfile?.DEA_SCORE?.finalScore <= 4);

    if (result?.isHardFail) {
      setStatus("rejected");
    } else if (
      highPointProfile?.DEA_SCORE?.finalScore > 4 &&
      !profilesHaveMissingDocs
    ) {
      setStatus("approved");
    } else {
      setStatus("review");
    }

    setHigherScoreProfile(highPointProfile);
    setShowLocalLoading(false);
  }, [tenantData]);

  const toggleResolveTenant = (tenantId) => {
    if (!tenantId) return;
    setSelectedResolveTenantIds((prev) =>
      prev.includes(tenantId)
        ? prev.filter((id) => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  // ── KYC unclear categories (tenant only) ─────────────────────
  useEffect(() => {
    const tenantsOnly = tenantData.filter((t) => t?.role !== "guarantor");
    if (!tenantsOnly.length) {
      setKycWarnings({});
      setIsKycWarningsLoading(false);
      return;
    }
    let cancelled = false;
    setIsKycWarningsLoading(true);
    const build = async () => {
      const warnings = {};
      try {
        await Promise.all(
          tenantsOnly.map(async (item) => {
            const tenantId = item?._id;
            if (!tenantId) return;
            const raw =
              rawTenantsById[tenantId] ||
              rawTenantsById[String(tenantId)] ||
              null;
            const userObj = raw || item;
            const kycUser = userObj?.onfido_user_kyc;
            // Only KYC records with check results can have unresolved items.
            if (!kycUser?.kyc_checks && !kycUser?.kyc_aml) return;
            let kycRes = null;
            try {
              kycRes = await getProfileResolutionStatus(tenantId, "kyc");
            } catch (e) {
              console.log("Failed to fetch KYC status for", tenantId, e);
              kycRes = null;
            }
            if (cancelled) return;
            const kycStatus = getKycResolutionStatus(kycUser, kycRes);
            const unresolved = kycStatus.categoryResults.nonClear.filter(
              (c) => !kycStatus.resolvedCategoryKeys.has(c.key)
            );
            if (unresolved.length === 0) return;
            warnings[tenantId] = {
              label: item.label || item?.fname || tenantId,
              unresolved,
              unresolvedCount: unresolved.length,
              tenantId,
            };
          })
        );
      } catch (e) {
        console.log("Failed to build KYC warnings", e);
      } finally {
        if (!cancelled) {
          setKycWarnings(warnings);
          setIsKycWarningsLoading(false);
        }
      }
    };
    build();
    return () => {
      cancelled = true;
    };
  }, [tenantData, rawTenantsById, dataRefreshKey, getProfileResolutionStatus]);

  const handleKycTenantClick = useCallback(
    (tenantId) => {
      const spId =
        idToSharedProfileId[tenantId] ||
        idToSharedProfileId[String(tenantId)] ||
        tenantData.find((t) => t._id === tenantId)?.sharedProfileId;
      if (!spId || !tenantId) return;
      const url = `/${workspaceId}/profile-property?sharedprofileid=${spId}&tenant_id=${tenantId}&kycAml=true`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [idToSharedProfileId, workspaceId, tenantData]
  );

  // selectedResolveTenantIds = current checked ids.
  // Resolved are checked by default; unchecking them queues an unresolve.
  const pendingResolveIds = useMemo(
    () =>
      selectedResolveTenantIds.filter(
        (id) => !individualWarnings?.[id]?.isResolved
      ),
    [selectedResolveTenantIds, individualWarnings]
  );
  const pendingUnresolveIds = useMemo(() => {
    const selectedSet = new Set(selectedResolveTenantIds);
    return Object.entries(individualWarnings || {})
      .filter(([, w]) => w?.isResolved)
      .map(([id]) => id)
      .filter((id) => !selectedSet.has(id));
  }, [selectedResolveTenantIds, individualWarnings]);

  const hasPendingChanges =
    pendingResolveIds.length > 0 || pendingUnresolveIds.length > 0;

  // Seed checked state with resolved ids on load/change
  useEffect(() => {
    const resolvedIds = Object.entries(individualWarnings || {})
      .filter(([, w]) => w?.isResolved)
      .map(([id]) => id);
    setSelectedResolveTenantIds((prev) => {
      const prevPendingResolve = prev.filter(
        (id) => !individualWarnings?.[id]?.isResolved
      );
      // keep user's pending resolves, always reflect current resolved as checked
      return [...new Set([...resolvedIds, ...prevPendingResolve])];
    });
  }, [individualWarnings]);
  const resolveActionLabel = useMemo(() => {
    const hasResolve = pendingResolveIds.length > 0;
    const hasUnresolve = pendingUnresolveIds.length > 0;
    if (hasResolve && hasUnresolve) return _t("update", "Update");
    if (hasUnresolve) return _t("unresolve", "Unresolve");
    return _t("markResolved", "Mark resolved");
  }, [pendingResolveIds, pendingUnresolveIds, _t]);

  const handleApplyResolutions = async (note) => {
    if (!hasPendingChanges) return;
    setResolvingDocs(true);
    try {
      const ops = [];
      pendingResolveIds.forEach((tenantId) =>
        ops.push(
          markProfileResolved({ tenant_id: tenantId, type: "documents", note })
        )
      );
      pendingUnresolveIds.forEach((tenantId) =>
        ops.push(unmarkProfileResolved(tenantId, "documents"))
      );
      await Promise.all(ops);
      if (pendingResolveIds.length && pendingUnresolveIds.length)
        toast.success(_t("documentsUpdated", "Documents updated"));
      else if (pendingUnresolveIds.length)
        toast.success(_t("documentsUnresolved", "Documents unresolved"));
      else
        toast.success(
          _t("documentsMarkedResolved", "Documents marked resolved")
        );
      setResolveNote("");
      setShowResolveNoteModal(false);
      setDataRefreshKey((currentKey) => currentKey + 1);
      onResolutionChangeProp?.({
        resolvedIds: pendingResolveIds,
        unresolvedIds: pendingUnresolveIds,
      });
    } catch (err) {
      console.log(err, "Error applying document resolutions");
      toast.error(_t("failedToUpdate", "Failed to update"));
    } finally {
      setResolvingDocs(false);
    }
  };

  // keep old name as alias for backwards compat if referenced elsewhere in file
  const handleMarkResolved = handleApplyResolutions;

  const handleGoBack = () => {
    if (redirectUrl) {
      navigate(redirectUrl);
    } else {
      navigate(-1);
    }
  };

  const downloadPdfAndDocuments = async () => {
    let hiddenElements = [];
    const previousDisplays = new Map();

    try {
      setShowSpinner(true);
      setIsPdfExporting(true);

      // Allow export-only content to render before capturing the report.
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      );

      const container = document.getElementById("divToPrint");
      if (!container) throw new Error("Report container not found");

      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;

      // hide buttons
      hiddenElements = Array.from(document.querySelectorAll(".no-print"));
      hiddenElements.forEach((el) => {
        previousDisplays.set(el, el.style.display);
        el.style.display = "none";
      });

      const pdf = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
      });

      const canvas = await html2canvas(container, {
        useCORS: true,
        backgroundColor: "var(--surface)",
        scale: 2,
        ignoreElements: (element) => element.classList.contains("no-print"),
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const scale = Math.min(A4_WIDTH / imgWidth, A4_HEIGHT / imgHeight);

      const renderWidth = imgWidth * scale;
      const renderHeight = imgHeight * scale;

      const x = (A4_WIDTH - renderWidth) / 2;
      const y = (A4_HEIGHT - renderHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight);

      // convert PDF to blob instead of downloading
      const pdfBlob = pdf.output("blob");

      hiddenElements.forEach((el) => {
        el.style.display = previousDisplays.get(el) ?? "";
      });
      hiddenElements = [];
      setIsPdfExporting(false);

      // -----------------------------
      // GET BACKEND ZIP
      // -----------------------------

      const allTenants = tenantData?.map((tenant) => tenant?._id);

      const res = await getZipOfMultipleTenantDocs(allTenants);

      const backendZipBlob = res.data;

      // -----------------------------
      // UNZIP BACKEND ZIP
      // -----------------------------

      const backendZip = await JSZip.loadAsync(backendZipBlob);

      // create final zip
      const finalZip = new JSZip();

      // add generated pdf
      finalZip.file("report_and_documents.pdf", pdfBlob);

      // add backend files
      const promises = [];

      backendZip.forEach((relativePath, file) => {
        if (!file.dir) {
          promises.push(
            file.async("blob").then((content) => {
              finalZip.file(relativePath, content);
            })
          );
        }
      });

      await Promise.all(promises);

      // -----------------------------
      // GENERATE FINAL ZIP
      // -----------------------------

      const finalZipBlob = await finalZip.generateAsync({
        type: "blob",
      });

      const url = window.URL.createObjectURL(finalZipBlob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "documents.zip";

      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error capturing element:", err);
    } finally {
      hiddenElements.forEach((el) => {
        el.style.display = previousDisplays.get(el) ?? "";
      });
      setIsPdfExporting(false);
      setShowSpinner(false);
    }
  };

  const getProfileIsResolved = async (profileId) => {
    if (!haveValue(profileId)) return null;
    try {
      const res = await getProfileResolutionStatus(profileId, "documents");
      return res?.isResolved ? res : null;
    } catch (err) {
      console.log("Failed to fetch profile resolution status", err);
      return null;
    }
  };

  const handleTenantResponse = async (response) => {
    let userIncomeObj = {};
    let tenantIncomeSum = 0;
    let guarantorIncomeSum = 0;

    // Participants flagged as guarantors via `guarantorid=` URL param are
    // stored as tenant participants in selectionModel. Score them as
    // guarantors, not tenants. Nested guarantors never match a response id,
    // so old links without the param behave exactly as before.
    const urlGuarantorIdSet = new Set(
      (urlGuarantorIds || []).map((id) => String(id))
    );
    const isUrlGuarantor = (t) =>
      urlGuarantorIdSet.has(String(t?._id ?? t?.id ?? t?.tenant_id ?? ""));
    const tenantResponse = response.filter((t) => !isUrlGuarantor(t));
    const guarantorParticipantResponse = response.filter((t) =>
      isUrlGuarantor(t)
    );

    // ---- Calculate Tenant + Guarantor Income ----
    for (let tenant of tenantResponse) {
      if (selectionSelectedIds && !selectionSelectedIds.includes(tenant?._id))
        continue;
      const incomeSelection = tenant?.income_selection;

      // Tenant income
      const tenantIncome = getCustomIncomeDetails(
        tenant,
        user,
        _t,
        incomeSelection?.tenant_source,
        undefined,
        undefined,
        deaWorkspace,
        tenant?.custom_income_sources,
        getSavedIncomeComponents(incomeSelection, { tenantId: tenant?._id })
      );

      const tenantAmount = tenantIncome?.amount ?? 0;
      userIncomeObj[tenant?._id] = tenantAmount;
      // effort rate type decides whose income counts
      const effortRateType = getEffortRateType(tenant, tenant);
      if (["tenant", "both"].includes(effortRateType)) {
        tenantIncomeSum += tenantAmount;
      }

      // Guarantor incomes
      let guarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        guarantors = guarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }
      const guarantorIncomeSelection =
        incomeSelection?.[tenant?._id]?.guarantor_sources ??
        incomeSelection?.guarantor_sources;

      for (let g of guarantors) {
        const guarantorIncome = getCustomIncomeDetails(
          g,
          user,
          _t,
          guarantorIncomeSelection?.[g?._id],
          undefined,
          undefined,
          deaWorkspace,
          tenant?.custom_income_sources,
          getSavedIncomeComponents(incomeSelection, {
            tenantId: tenant?._id,
            userId: g?._id,
            isGuarantor: true,
          })
        );

        userIncomeObj[g?._id] = guarantorIncome?.amount ?? 0;
        if (["guarantor", "both"].includes(effortRateType)) {
          guarantorIncomeSum += guarantorIncome?.amount ?? 0;
        }
      }
    }

    // Participant-guarantors: count their own income as guarantor income.
    for (let tenant of guarantorParticipantResponse) {
      if (selectionSelectedIds && !selectionSelectedIds.includes(tenant?._id))
        continue;
      const participantIncome = getCustomIncomeDetails(
        tenant,
        user,
        _t,
        tenant?.income_selection?.tenant_source,
        undefined,
        undefined,
        deaWorkspace,
        tenant?.custom_income_sources,
        getSavedIncomeComponents(tenant?.income_selection, {
          tenantId: tenant?._id,
        })
      );
      const participantAmount = participantIncome?.amount ?? 0;
      userIncomeObj[tenant?._id] = participantAmount;
      guarantorIncomeSum += participantAmount;
    }

    // Total income from tenant + guarantors
    const totalIncome = tenantIncomeSum + guarantorIncomeSum;

    const finalEffortRate =
      getRentPercentage(totalIncome, rentVal)?.roundValue ?? "0";

    const result = [];
    const resolutionsById = {};

    for (let idx = 0; idx < tenantResponse.length; idx++) {
      const tenant = tenantResponse[idx];
      if (selectionSelectedIds && !selectionSelectedIds.includes(tenant?._id))
        continue;
      const tenantSharedProfileId =
        selectedProfileIdMap?.[tenant?._id] ??
        selectedProfileIdMap?.[String(tenant?._id)] ??
        sharedprofileid[idx] ??
        sharedprofileid[0];

      // ----- TENANT PROCESSING -----
      const tenantResolution = await getProfileIsResolved(tenant?._id);
      if (tenantResolution) resolutionsById[tenant?._id] = tenantResolution;
      const tenantIsResolved = !!tenantResolution;
      const temp = await getFieldForPolicies(
        tenant,
        user,
        finalEffortRate,
        false,
        deaWorkspace
      );

      const DEA_SCORE = calculateOverlayScore(
        temp.EFFORT_RATE_SCORE,
        temp.TENURE,
        temp.EMPLOYMENT_PREDICTABILITY,
        temp.SECTOR_CYCLICALITY,
        temp.EMPLOYER_STABILITY
      );

      const errorEntries = Object.entries(temp).filter(
        ([key, value]) => value?.isHardFail === true
      );

      let guarantorOutsideSpain = false;
      let checkGuarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        checkGuarantors = checkGuarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }
      for (let g of checkGuarantors) {
        const forensicData = g?.tenant_extra_field_id?.forensic_financial_data;
        if (forensicData) {
          for (let k in forensicData) {
            if (
              forensicData[k]?.origin &&
              forensicData[k]?.origin !== "Local"
            ) {
              guarantorOutsideSpain = true;
              break;
            }
          }
        }
        if (guarantorOutsideSpain) break;
      }
      if (
        guarantorOutsideSpain &&
        isGuarantorOutsideSpainHardFail(user, deaWorkspace)
      ) {
        errorEntries.push([
          "GUARANTOR_OUTSIDE_SPAIN",
          { isHardFail: true, label: "guarantorLivingOutsideSpain" },
        ]);
      }

      const label = getUserDisplayName(tenant);

      tenant.label = label;

      result.push({
        ...tenant,
        role: "tenant",
        sharedProfileId: tenantSharedProfileId,
        DEA_SCORE,
        errorEntries,
        vals: temp,
        income: userIncomeObj?.[tenant?._id],
        hasMissingDniNie: tenantHasMissingDniNie(tenant),
        isResolved: tenantIsResolved,
      });

      // ----- GUARANTOR PROCESSING (same as tenant) -----
      let guarantors = tenant?.gurantor_id || [];
      if (selectionSelectedGuarantorIds) {
        guarantors = guarantors.filter((g) =>
          selectionSelectedGuarantorIds.includes(g?._id)
        );
      }

      for (let g of guarantors) {
        const gResolution = await getProfileIsResolved(g?._id);
        if (gResolution) resolutionsById[g?._id] = gResolution;
        const gIsResolved = !!gResolution;
        const gTemp = await getFieldForPolicies(
          g,
          user,
          finalEffortRate,
          false,
          deaWorkspace
        );

        const gDEA_SCORE = calculateOverlayScore(
          gTemp.EFFORT_RATE_SCORE,
          gTemp.TENURE,
          gTemp.EMPLOYMENT_PREDICTABILITY,
          gTemp.SECTOR_CYCLICALITY,
          gTemp.EMPLOYER_STABILITY
        );

        const gErrorEntries = Object.entries(gTemp).filter(
          ([key, value]) => value?.isHardFail === true
        );

        const gLabel = getUserDisplayName(g);

        g.label = gLabel;

        result.push({
          ...g,
          role: "guarantor",
          sharedProfileId: tenantSharedProfileId,
          parentTenantId: tenant?._id,
          DEA_SCORE: gDEA_SCORE,
          errorEntries: gErrorEntries,
          vals: gTemp,
          income: userIncomeObj?.[g?._id],
          hasMissingDniNie: tenantHasMissingDniNie(g),
          isResolved: gIsResolved,
        });
      }
    }

    // ----- PARTICIPANT-GUARANTOR PROCESSING -----
    // Application participants flagged as guarantors score as guarantors.
    for (let tenant of guarantorParticipantResponse) {
      if (selectionSelectedIds && !selectionSelectedIds.includes(tenant?._id))
        continue;
      const tenantSharedProfileId =
        selectedProfileIdMap?.[tenant?._id] ??
        selectedProfileIdMap?.[String(tenant?._id)] ??
        sharedprofileid[0];
      const gResolution = await getProfileIsResolved(tenant?._id);
      if (gResolution) resolutionsById[tenant?._id] = gResolution;
      const gTemp = await getFieldForPolicies(
        tenant,
        user,
        finalEffortRate,
        false,
        deaWorkspace
      );
      const gDEA_SCORE = calculateOverlayScore(
        gTemp.EFFORT_RATE_SCORE,
        gTemp.TENURE,
        gTemp.EMPLOYMENT_PREDICTABILITY,
        gTemp.SECTOR_CYCLICALITY,
        gTemp.EMPLOYER_STABILITY
      );
      const gErrorEntries = Object.entries(gTemp).filter(
        ([key, value]) => value?.isHardFail === true
      );
      const gLabel = getUserDisplayName(tenant);
      tenant.label = gLabel;
      result.push({
        ...tenant,
        role: "guarantor",
        sharedProfileId: tenantSharedProfileId,
        parentTenantId: null,
        DEA_SCORE: gDEA_SCORE,
        errorEntries: gErrorEntries,
        vals: gTemp,
        income: userIncomeObj?.[tenant?._id],
        hasMissingDniNie: tenantHasMissingDniNie(tenant),
        isResolved: !!gResolution,
      });
    }

    setTenantData(result);
    setIndividualResolutions(resolutionsById);
  };

  const goToReportPage = () => {
    const params = new URLSearchParams();
    if (haveValue(property_id)) params.set("property", property_id);
    sharedprofileid.forEach((id) => params.append("sharedprofileid", id));
    if (haveValue(rentVal)) params.set("rent", rentVal);
    if (haveValue(redirectUrl)) params.set("redirectUrl", redirectUrl);
    if (haveValue(selectionRaw)) params.set("selection", selectionRaw);
    try {
      const currentParams = new URLSearchParams(window.location.search);
      ["guarantorid", "guarantorId", "guarantorids", "guarantorIds"].forEach(
        (key) => {
          currentParams.getAll(key).forEach((id) => {
            if (haveValue(id)) params.append("guarantorid", id);
          });
        }
      );
    } catch {
      // ignore malformed URL when forwarding guarantor ids
    }
    if (awardSnapshotCompleted) params.set("awardSnapshotCompleted", "true");
    if (haveValue(historyEventId)) params.set("historyEventId", historyEventId);
    navigate(
      `/${workspaceId}/generatedReport/combinedReport?${params.toString()}`
    );
  };

  const handleProfileClick = (tenant) => {
    const baseUrl = `/${workspaceId}/profile-property?sharedprofileid=${tenant.sharedProfileId}`;
    const url =
      tenant.role === "tenant"
        ? `${baseUrl}&tenant_id=${tenant._id}`
        : `${baseUrl}&guarantor=${tenant._id}`;
    window.open(url, "_blank");
  };

  const formatMissingDoc = (val) => {
    const payslipMatch = val.match(/^payslip\s*-\s*(.+)$/i);
    if (payslipMatch) {
      return `${_t("Payslip")} - ${payslipMatch[1]}`;
    }
    return _t(val);
  };

  const resolvedWarnings = Object.entries(individualWarnings || {}).filter(
    ([, warning]) => warning?.isResolved
  );

  const hardFailWarnings = useMemo(
    () => buildHardFailWarnings(tenantData || []),
    [tenantData]
  );

  const blockingRows = useMemo(() => {
    const base = buildBlockingRows(
      hardFailWarnings,
      _t,
      status === "rejected",
      []
    );
    const rows = [...base];
    // low score blocking (collectiveScoreIsLessThan4)
    if (lowScoreCheck) {
      const hasThreshold = rows.some(
        (row) =>
          row.key === "threshold" ||
          row.title?.toLowerCase().includes("threshold") ||
          row.title?.toLowerCase().includes("score below")
      );
      if (!hasThreshold) {
        const disp = getHardFailDisplay(
          "scoreBelowThreshold",
          "scoreBelowThreshold",
          _t
        );
        rows.push({
          key: "lowScore",
          title: disp.title,
          desc: disp.desc,
        });
      }
    }
    // missing DNI/NIE as blocking
    const dniNames = Object.keys(missingDniNieWarning || {});
    if (dniNames.length > 0) {
      const alreadyHasKyc = rows.some((row) =>
        row.title?.toLowerCase().includes("identification")
      );
      if (alreadyHasKyc) {
        // append affected names to existing identification row
        const idx = rows.findIndex((row) =>
          row.title?.toLowerCase().includes("identification")
        );
        if (idx >= 0) {
          const existing = rows[idx];
          const extra = `${_t("affectedApplicants", "Affected applicants")}: ${dniNames.join(", ")}`;
          rows[idx] = {
            ...existing,
            desc: existing.desc ? `${existing.desc} ${extra}` : extra,
          };
        }
      } else {
        const disp = getHardFailDisplay("KYCHardFail", "KYCHardFail", _t);
        rows.push({
          key: "missingDniNie",
          title: disp.title,
          desc: `${disp.desc} ${_t("affectedApplicants", "Affected applicants")}: ${dniNames.join(", ")}`,
        });
      }
    }
    return rows;
  }, [hardFailWarnings, status, lowScoreCheck, missingDniNieWarning, _t]);

  return showLocalLoading || isSimulationLoading ? (
    <div className="global-loader">
      <div className="child-loader">
        <img src={SkorDefaultLoader} className="defaultSkorLoader" alt="" />
        <p style={{ fontSize: "0.833vw", color: "#7F7F7F" }}>
          {showLocalLoading && _t("generatingReport")}
          {showLocalLoading && isSimulationLoading && <> {_t("and")} </>}
          {isSimulationLoading && _t("analysingFeasibility")}
        </p>
      </div>
    </div>
  ) : (
    <>
      <div className="report-overview-comp" id="divToPrint">
        {/* Header */}
        <div className="report-overview__header">
          <Button
            variant="ghost"
            size="sm"
            className="report-overview__back no-print"
            onClick={handleGoBack}
            iconStart={<Icon name="chevronLeft" />}
          >
            {_t("goBack")}
          </Button>

          <PageHeader
            title={[
              `${addressVal?.street ?? ""} ${
                addressVal?.street_number ?? ""
              }`.trim(),
              `${addressVal?.floor_number ? addressVal?.floor_number : ""} ${
                addressVal?.flat_number ? "-" + addressVal?.flat_number : ""
              }`,

              addressVal?.province,
            ]
              .filter(Boolean)
              .join(", ")}
          />

          <div className="report-overview__subinfo">
            <span>
              {[addressVal?.postcode, addressVal?.muncipality]
                .filter(Boolean)
                .join(", ")}
              {todaysDate(
                awardSnapshotCompleted || historyEventId
                  ? addressVal?.award_snapshot_info?.created_at
                  : null
              )}
            </span>
            <span className="report-overview__dot">•</span>
            <span>€{rentVal}/month</span>
          </div>

          <div className="report-overview__meta">
            <div className="report-overview__meta-item">
              <img src={"/images/calendar-icon.svg"} alt="" />
              <span>
                {todaysDate(addressVal?.award_snapshot_info?.created_at)}
              </span>
            </div>
            <div className="report-overview__meta-item">
              <img src={"/images/multiple-user.svg"} alt="" />{" "}
              <span>
                {" "}
                {sharedprofileid?.length} {_t("tenantsSelected")}
              </span>
            </div>
          </div>
        </div>

        {/* Approval Card */}
        <div
          style={STATUS_STYLES[status]}
          className="report-overview__approval-card"
        >
          <div className="report-overview__approval-status">
            <img src={STATUS_STYLES[status].img} />
            <span style={{ color: STATUS_STYLES[status].color }}>
              {STATUS_STYLES[status].text}
            </span>
          </div>

          <div className="report-overview__approval-metrics">
            <div>
              <strong>
                {_t("finalScore")}: {higherScoreProfile?.DEA_SCORE?.finalScore}
              </strong>{" "}
              / 10
            </div>

            <div>
              <strong>
                {_t("combinedEffortRate")}:{" "}
                {tenantData[0]?.vals?.EFFORT_RATE_SCORE?.EFFORT_RATE_SCORE}%
              </strong>
            </div>

            {insuranceState ? (
              <InsurabilityBadge
                status={insuranceState.status}
                errorCode={insuranceState.errorCode}
                errorCodes={insuranceState.errorCodes}
                preFilledInsuranceDocument={
                  insuranceState.preFilledInsuranceDocument
                }
                contactOnly={insuranceState.contactOnly}
              />
            ) : (
              <div className="report-overview__insurable">
                {tenantData?.some(
                  (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
                ) ? (
                  <img src={"/images/not-insurable.svg"} alt="" />
                ) : (
                  <img src={"/images/shield.svg"} alt="" />
                )}
                <span
                  style={{
                    color: tenantData?.some(
                      (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
                    )
                      ? "var(--danger-text)"
                      : "var(--ink)",
                  }}
                >
                  {_t(
                    tenantData?.some(
                      (item) => item?.vals?.DEFAULT_HISTORY?.isHardFail === true
                    )
                      ? "notInsurable"
                      : "insurable"
                  )}
                </span>
              </div>
            )}
          </div>

          <p className="report-overview__approval-text">
            {_t(
              status === "rejected"
                ? "oneOrMoreTenantsDoNotMeetTheRequiredRligibilityCriteriaForThisRental"
                : status === "review"
                  ? "someTenantsRequireAdditionalVerificationBeforeTheRentalCanBeApproved"
                  : "allSelectedTenantsMeetTheEligibilityCriteriaAndQualifyForInsuranceCoverage"
            )}
          </p>
          {(status === "rejected" ||
            status === "review" ||
            resolvedWarnings.length > 0 ||
            blockingRows.length > 0 ||
            Object.keys(individualWarnings || {}).length > 0 ||
            Object.keys(kycWarnings || {}).length > 0 ||
            isVerdictLoading) && (
            <div className="report-overview-declined-decision">
              <h1>{_t("whyThisDecision")}?</h1>
              <VerdictPanel
                _t={_t}
                individualWarnings={individualWarnings}
                individualResolutions={individualResolutions}
                selectedResolveTenantIds={selectedResolveTenantIds}
                toggleResolveTenant={toggleResolveTenant}
                formatMissingDoc={formatMissingDoc}
                expandedDocs={expandedDocs}
                forceExpandDocs={isPdfExporting}
                toggleDocsExpanded={toggleDocsExpanded}
                hasPendingChanges={hasPendingChanges}
                resolvingDocs={resolvingDocs}
                resolveActionLabel={resolveActionLabel}
                onResolveClick={() => setShowResolveNoteModal(true)}
                blockingRows={blockingRows}
                guarantorItems={[]}
                kycWarnings={kycWarnings}
                onKycTenantClick={handleKycTenantClick}
                isVerdictLoading={isVerdictLoading}
              />
            </div>
          )}
        </div>

        {/* Individual Tenant Scores */}
        <h2 className="report-overview__section-title">
          {_t("individualTenantScores")}
        </h2>
        {tenantData?.map((tenant) => (
          <div className="report-overview__tenant-card-comp">
            <div className="report-overview__tenant-card">
              <div className="report-overview__tenant-header">
                <Avatar name={tenant?.label} size="lg" />

                <div className="report-overview__tenant-info">
                  <h3
                    className={"d-flex align-items-center gap-1 cursor-pointer"}
                    onClick={() => handleProfileClick(tenant)}
                  >
                    <span>{tenant?.label ?? ""}</span>
                    <Badge tone="neutral">{_t(tenant?.role)}</Badge>
                    <img
                      src="/images/external-link.svg"
                      alt=""
                      className="no-print"
                      style={{
                        width: "0.833vw",
                        height: "0.833vw",
                        marginLeft: "0.347vw",
                        verticalAlign: "middle",
                      }}
                    />
                  </h3>
                  <div className="report-overview__tenant-meta">
                    {haveValue(tenant?.paymentScores?.creditScore?.value) && (
                      <>
                        <span>
                          {_t("skor")}:{" "}
                          {tenant?.paymentScores?.creditScore?.value}
                        </span>
                        <span>•</span>
                      </>
                    )}
                    <span>
                      {_t("incomeVal")}: {tenant?.income}
                    </span>
                  </div>
                </div>

                <div className="report-overview__score">
                  <div className="report-overview__score-value">
                    {tenant?.DEA_SCORE?.finalScore}
                  </div>
                  <div className="report-overview__score-label">
                    {_t("score")}
                  </div>
                </div>
              </div>

              {/* Tenant Details */}
              <div className="report-overview__tenant-details">
                <div className="report-overview__detail-row">
                  <span>{_t("debtRatio")}</span>
                  <span style={{ color: "var(--ink)" }}>
                    {tenant?.DEA_SCORE?.EFFORT_RATE?.label}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.EFFORT_RATE?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.EFFORT_RATE?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.EFFORT_RATE?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.EFFORT_RATE?.score}
                  </b>
                </div>

                <div className="report-overview__detail-row">
                  <span>{_t("seniority")}</span>
                  <span style={{ color: "var(--ink)" }}>
                    {_t(tenant?.DEA_SCORE?.TENURE?.label)}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.TENURE?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.TENURE?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.TENURE?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.TENURE?.score}
                  </b>
                </div>

                <div className="report-overview__detail-row">
                  <span>{_t("incomeType")}</span>
                  <span style={{ color: "var(--ink)" }}>
                    {_t(tenant?.DEA_SCORE?.INCOME_TYPE?.label)}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.INCOME_TYPE?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.INCOME_TYPE?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.INCOME_TYPE?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.INCOME_TYPE?.score}
                  </b>
                </div>

                <div className="report-overview__detail-row">
                  <span>{_t("employmentType")}</span>
                  <span style={{ color: "var(--ink)" }}>
                    {_t(tenant?.DEA_SCORE?.CONTRACT_TYPE?.label)}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.CONTRACT_TYPE?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.CONTRACT_TYPE?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.CONTRACT_TYPE?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.CONTRACT_TYPE?.score}
                  </b>
                </div>

                <div
                  style={{ borderBottom: "none" }}
                  className="report-overview__detail-row"
                >
                  <span>{_t("riskSector")}</span>
                  <span style={{ color: "var(--ink)" }}>
                    {_t(tenant?.DEA_SCORE?.RISK_SECTORS?.label)}{" "}
                  </span>
                  <b
                    className={
                      tenant?.DEA_SCORE?.RISK_SECTORS?.score > 0
                        ? "plus"
                        : tenant?.DEA_SCORE?.RISK_SECTORS?.score < 0
                          ? "minus"
                          : "neutral"
                    }
                  >
                    {tenant?.DEA_SCORE?.RISK_SECTORS?.score > 0 && "+"}
                    {tenant?.DEA_SCORE?.RISK_SECTORS?.score}
                  </b>
                </div>
              </div>

              {haveValue(individualError?.[tenant?.label]) &&
                (() => {
                  const descs = (individualError?.[tenant?.label] || []).map(
                    (val) => _t(`${val[1]?.label}DescInOverview`)
                  );
                  const subtitle = descs.join(", ");
                  const rowKey = `tenant-err:${tenant?._id || tenant?.label}`;
                  const expanded =
                    isPdfExporting || !!expandedTenantRows[rowKey];
                  return (
                    <div className="report-overview-declined-review-info-comp">
                      <div className="verdict-panel__row">
                        <span
                          className="verdict-panel__lock verdict-panel__lock--blocking"
                          aria-hidden="true"
                        >
                          <LockIcon />
                        </span>
                        <div className="verdict-panel__row-content">
                          <VerdictInlineLine
                            title={withColon(_t("rejected", "Rejected"))}
                            subtitle={subtitle}
                            expanded={expanded}
                            onToggle={() => toggleTenantRow(rowKey)}
                            expandLabel={_t("expandDetails", "Expand details")}
                            collapseLabel={_t("showLess", "Show less")}
                            hideToggle={isPdfExporting}
                            dotsClassName="verdict-panel__dots no-print"
                          />
                          {expanded && subtitle ? (
                            <div className="verdict-panel__inline-full">
                              {subtitle}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              {haveValue(individualWarnings?.[tenant?._id]) &&
                (() => {
                  const docs =
                    individualWarnings?.[tenant?._id]?.docsMissing || [];
                  const subtitle = docs.map(formatMissingDoc).join(", ");
                  const rowKey = `tenant-missing:${tenant?._id}`;
                  const expanded =
                    isPdfExporting || !!expandedTenantRows[rowKey];
                  return (
                    <div className="report-overview-declined-review-info-comp">
                      <div className="verdict-panel__row">
                        <span
                          className="verdict-panel__lock"
                          aria-hidden="true"
                        >
                          <InfoIcon />
                        </span>
                        <div className="verdict-panel__row-content">
                          <VerdictInlineLine
                            title={withColon(_t("missingDocuments"))}
                            subtitle={subtitle}
                            expanded={expanded}
                            onToggle={() => toggleTenantRow(rowKey)}
                            expandLabel={_t("expandDetails", "Expand details")}
                            collapseLabel={_t("showLess", "Show less")}
                            hideToggle={isPdfExporting}
                            dotsClassName="verdict-panel__dots no-print"
                          />
                          {expanded && subtitle ? (
                            <div className="verdict-panel__inline-full">
                              {subtitle}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        ))}
        <div className="report-overview-footer no-print">
          <p>{_t("clickOnAnyTenantToViewDetailedScoringBreakdown")}</p>
          <div>
            <Button
              variant="accent"
              loading={showSpinner}
              onClick={downloadPdfAndDocuments}
              iconStart={<Icon name="download" />}
            >
              {showSpinner ? `${_t("downloading")}...` : _t("downloadReport")}
            </Button>
            <Button onClick={goToReportPage}>{_t("viewFullDetails")}</Button>
          </div>
        </div>
      </div>

      <Modal
        open={showResolveNoteModal}
        onClose={() => setShowResolveNoteModal(false)}
        title={resolveActionLabel}
        align="start"
        actionsLayout="end"
        actions={
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setShowResolveNoteModal(false)}
            >
              {_t("cancel")}
            </Button>
            <Button
              size="lg"
              loading={resolvingDocs}
              onClick={() => handleApplyResolutions(resolveNote)}
            >
              {resolvingDocs
                ? `${_t("updating", "Updating")}...`
                : resolveActionLabel}
            </Button>
          </>
        }
      >
        <p className="resolve-documents-modal__summary">
          {pendingResolveIds.length > 0 && pendingUnresolveIds.length > 0
            ? _t("updatingDocumentsFor", "Updating documents for")
            : pendingUnresolveIds.length > 0
              ? _t("unresolvingDocumentsFor", "Unresolving documents for")
              : _t("resolvingDocumentsFor", "Resolving documents for")}{" "}
          <strong>{selectedResolveTenantIds.length}</strong>{" "}
          {_t("tenantS", "tenant(s)")}
        </p>
        {pendingResolveIds.length > 0 && (
          <div className="resolve-documents-modal__field">
            <TextArea
              id="report-resolve-note"
              placeholder={_t("addNote", "Add a note (optional)")}
              aria-label={_t("addNote", "Add a note (optional)")}
              maxLength={500}
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
            />
            <span className="resolve-documents-modal__count">
              {resolveNote?.length}/500
            </span>
          </div>
        )}
        {pendingUnresolveIds.length > 0 && pendingResolveIds.length === 0 && (
          <p className="resolve-documents-modal__hint">
            {_t(
              "unresolveHint",
              "This will mark the documents as unresolved again."
            )}
          </p>
        )}
      </Modal>
    </>
  );
};

export default ReportOverview;
